import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LogIn, ArrowLeft, Volume2, BrainCircuit, Repeat, Headphones, HeartPulse,
  Plane, Banknote, Truck, Building2, Quote,
} from 'lucide-react';

const ETYMOLOGY = [
  { part: 'Vo', icon: <Volume2 className="w-5 h-5" />, meaning: 'voice / vocal' },
  { part: 'Ken', icon: <BrainCircuit className="w-5 h-5" />, meaning: 'knowledge / intelligence' },
  { part: 'Tra', icon: <Repeat className="w-5 h-5" />, meaning: 'transaction / transformation / travel across industries' },
];

const LINES = [
  { name: 'Vokentra Telco', icon: <Headphones className="w-5 h-5" />, color: '#e60000', tag: 'Telecom' },
  { name: 'Vokentra Care', icon: <HeartPulse className="w-5 h-5" />, color: '#10b981', tag: 'Healthcare' },
  { name: 'Vokentra Travel', icon: <Plane className="w-5 h-5" />, color: '#3b82f6', tag: 'Travel' },
  { name: 'Vokentra Finance', icon: <Banknote className="w-5 h-5" />, color: '#f59e0b', tag: 'Finance' },
  { name: 'Vokentra Logistics', icon: <Truck className="w-5 h-5" />, color: '#06b6d4', tag: 'Logistics' },
  { name: 'Vokentra Enterprise', icon: <Building2 className="w-5 h-5" />, color: '#a855f7', tag: 'Enterprise' },
];

function TopNav() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/70 border-b border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-vokentra flex items-center justify-center shadow-lg shadow-vokentra/30">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="white" />
              <circle cx="12" cy="12" r="5" fill="white" />
            </svg>
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold text-text-primary">Vokentra AI</div>
            <div className="text-[10px] text-text-muted">Voice Agents Platform</div>
          </div>
        </Link>
        <Link to="/login" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-vokentra text-white text-sm font-medium hover:bg-vokentra-dark transition-colors shadow-lg shadow-vokentra/30">
          <LogIn className="w-4 h-4" /> Login
        </Link>
      </div>
    </header>
  );
}

export function Company() {
  return (
    <div className="min-h-screen bg-background text-text-primary">
      <TopNav />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-grid-pattern opacity-40" />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% -10%, rgba(230,0,0,0.14) 0%, transparent 60%)' }} />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Back to home</Link>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-5xl font-extrabold tracking-tight">Vokentra AI</motion.h1>
          <p className="mt-3 text-text-secondary">Pronunciation: <span className="text-text-primary font-medium">Voh-ken-trah AI</span></p>
          <p className="mt-2 text-text-muted">Vokentra AI Voice Agents Platform · A product of Big Data Science &amp; Technology Limited</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 pb-24 space-y-12">
        {/* Why it works — etymology */}
        <section>
          <h2 className="text-xl font-bold mb-4">Why the name works</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {ETYMOLOGY.map((e, i) => (
              <motion.div key={e.part} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6">
                <div className="w-11 h-11 rounded-xl bg-vokentra/15 text-vokentra flex items-center justify-center mb-3">{e.icon}</div>
                <div className="text-2xl font-extrabold text-text-primary">{e.part}<span className="text-vokentra">·</span></div>
                <p className="text-sm text-text-muted mt-1">{e.meaning}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Brand package */}
        <section>
          <h2 className="text-xl font-bold mb-4">Brand package</h2>
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] divide-y divide-white/[0.06]">
            {[
              ['Product name', 'Vokentra AI'],
              ['Full name', 'Vokentra AI Voice Agents Platform'],
              ['Company line', 'A product of Big Data Science & Technology Limited'],
              ['Tagline', '“Intelligent voice agents for real-world operations.”'],
            ].map(([k, v]) => (
              <div key={k} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-5 py-4">
                <div className="w-40 shrink-0 text-xs uppercase tracking-wider text-text-muted">{k}</div>
                <div className="text-text-primary">{v}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Industry product lines */}
        <section>
          <h2 className="text-xl font-bold mb-4">Industry product lines</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {LINES.map((l, i) => (
              <motion.div key={l.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
                className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: l.color }}>{l.icon}</div>
                <div>
                  <div className="font-semibold text-text-primary">{l.name}</div>
                  <div className="text-[11px] text-text-muted">{l.tag}</div>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="text-sm text-text-muted mt-4">
            Among these, the <span className="text-text-primary font-medium">Vokentra Telco</span> agentic voice AI product is available today in this console.
          </p>
        </section>

        {/* Positioning */}
        <section>
          <div className="rounded-3xl border border-white/[0.08] p-8 relative overflow-hidden"
               style={{ background: 'radial-gradient(ellipse 80% 100% at 0% 0%, rgba(168,85,247,0.12), rgba(230,0,0,0.06))' }}>
            <Quote className="w-8 h-8 text-vokentra/60 mb-3" />
            <p className="text-lg sm:text-xl text-text-primary leading-relaxed">
              Vokentra AI, a product of Big Data Science &amp; Technology Limited, is a multi-agent voice
              intelligence platform that resolves customer and operational problems across
              <span className="text-vokentra"> telecom, healthcare, travel, finance, logistics, and enterprise</span> service sectors.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-vokentra text-white font-medium hover:bg-vokentra-dark transition-colors shadow-xl shadow-vokentra/30">
            <LogIn className="w-4 h-4" /> Login to the Vokentra console
          </Link>
        </section>
      </div>

      <footer className="border-t border-white/[0.06] px-6 py-8 text-center text-xs text-text-muted">
        © {new Date().getFullYear()} Vokentra AI · A product of Big Data Science &amp; Technology Limited
      </footer>
    </div>
  );
}

export default Company;
